import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Check, Search } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';
import { useAuth } from '../../../contexts/AuthContext';
import BotAvatar from '../../bots/BotAvatar';
import { botDisplayRole, useBotLabel } from '../../bots/botDisplay';
import BotName from '../../bots/BotName';

// Bots sit next to people: handing a task to a bot is just assigning it.
// value/onChange are the human assignees; botId/onBotChange the task's bot.
const AssigneePicker = ({
  value = [], teamMembers = [], onChange, children, disabled = false,
  bots = [], botId = null, onBotChange,
}) => {
  const botLabel = useBotLabel();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { user } = useAuth();

  const toggleMember = (userId) => {
    const next = value.includes(userId)
      ? value.filter(id => id !== userId)
      : [...value, userId];
    onChange(next);
  };

  const filtered = teamMembers.filter(m =>
    !search || m.full_name?.toLowerCase().includes(search.toLowerCase()) || m.email?.toLowerCase().includes(search.toLowerCase())
  );

  const currentBot = bots.find((b) => b.id === botId);

  const getHumanName = () => {
    if (value.length === 0) return t('tasks.filters.unassigned');
    if (value.length === 1) {
      if (value[0] === user?.id) return t('tasks.me');
      const member = teamMembers.find(m => m.user_id === value[0]);
      return member?.full_name?.split(' ')[0] || t('common.member');
    }
    return t('ui_task.assignee.members_count', { count: value.length });
  };

  const defaultTrigger = (
    <button disabled={disabled} className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] transition-colors ${
      disabled ? 'opacity-50 cursor-not-allowed' :
      value.length > 0 || currentBot ? 'text-purple-400 bg-purple-500/10' : 'text-muted-foreground hover:bg-accent/60'
    }`}>
      {currentBot ? (
        <>
          <BotAvatar bot={currentBot} size={16} />
          <span>{botLabel(currentBot)}</span>
          {value.length > 0 && <span className="text-muted-foreground">+ {getHumanName()}</span>}
        </>
      ) : (
        <>
          <Users className="w-3.5 h-3.5" />
          <span>{getHumanName()}</span>
        </>
      )}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || defaultTrigger}
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 bg-accent border-border max-md:!w-screen max-md:!max-w-none max-md:!rounded-b-none" align="start" sideOffset={4}>
        {teamMembers.length > 5 && (
          <div className="mb-2">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-accent/60 rounded-md">
              <Search className="w-3.5 h-3.5 text-muted-foreground" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('tasks.search_placeholder')}
                className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
                autoFocus
              />
            </div>
          </div>
        )}
        <div className="max-h-[280px] overflow-y-auto space-y-0.5">
          {/* Self first */}
          {user && (
            <button
              onClick={() => toggleMember(user.id)}
              className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md hover:bg-accent/60 text-sm ${value.includes(user.id) ? 'bg-accent/60' : ''}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-[10px] text-white font-medium">
                  {user.full_name?.[0]?.toUpperCase() || 'U'}
                </div>
                <span className="text-foreground/80">{t('tasks.me')}</span>
              </div>
              {value.includes(user.id) && <Check className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400" />}
            </button>
          )}
          {filtered.filter(m => m.user_id !== user?.id).map(member => (
            <button
              key={member.user_id}
              onClick={() => toggleMember(member.user_id)}
              className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md hover:bg-accent/60 text-sm ${value.includes(member.user_id) ? 'bg-accent/60' : ''}`}
            >
              <div className="flex items-center gap-2">
                {member.avatar_url ? (
                  <img src={member.avatar_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-[10px] text-white font-medium">
                    {member.full_name?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
                <span className="text-foreground/80 truncate">{member.full_name}</span>
              </div>
              {value.includes(member.user_id) && <Check className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400" />}
            </button>
          ))}
          {bots.length > 0 && onBotChange && (
            <>
              <div className="px-2.5 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t('bots.sidebar_title')}
              </div>
              {bots.map((bot) => (
                <button
                  key={bot.id}
                  onClick={() => onBotChange(botId === bot.id ? null : bot.id)}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md hover:bg-accent/60 text-sm ${botId === bot.id ? 'bg-accent/60' : ''}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <BotAvatar bot={bot} size={20} />
                    <div className="min-w-0 text-left">
                      <BotName bot={bot} className="block text-foreground/80" />
                      {botDisplayRole(bot, t) && (
                        <div className="text-[11px] text-muted-foreground truncate">{botDisplayRole(bot, t)}</div>
                      )}
                    </div>
                  </div>
                  {botId === bot.id && <Check className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400 shrink-0" />}
                </button>
              ))}
            </>
          )}
          {teamMembers.length === 0 && bots.length === 0 && (
            <div className="px-2.5 py-3 text-center text-xs text-muted-foreground">
              {t('ui_task.assignee.select_team_project')}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AssigneePicker;
